import { useApolloClient } from '@apollo/client';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, ListGroup, Modal, ModalProps, Spinner } from 'react-bootstrap';
import { LiveShowLogsQuery } from '../../../../../../__gqltypes__';
import { GET_LIVE_SHOW_LOGS } from '../graphql';
import LiveShowLogsConsolePopup from '../console/consolePopup';

const LOGS_PER_PAGE = 100;
type LiveShowLog = LiveShowLogsQuery['analytics']['liveShowLogs']['list'][0];
type DetailsProps = {
  log: LiveShowLog | null;
} & ModalProps;

const LiveShowLogDetails = ({ onHide, show, log }: DetailsProps) => {
  return (
    <Modal show={show} onHide={onHide} size="lg" className="live-show-log-details-modal">
      <Modal.Header closeButton>
        <Modal.Title>Log details</Modal.Title>
      </Modal.Header>
      <Modal.Body className="live-show-log-details-body">
        <pre className="w-auto live-show-log-details-pre">{JSON.stringify(log, null, 2)}</pre>
      </Modal.Body>
    </Modal>
  );
};

const LiveShowLogs = ({ showId }: { showId: string }) => {
  const client = useApolloClient();
  const [pageCount, setPageCount] = useState(1);
  const [pages, setPages] = useState<LiveShowLog[][]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [displayLogConsole, setDisplayLogConsole] = useState(false);

  const [loading, setLoading] = useState(false);
  const loadNextPage = useCallback(async () => {
    console.log('loadNextPage called');
    if (loading || pages.length >= pageCount) {
      return;
    }
    const pageIdx = pages.length;
    console.log(`Loading page ${pageIdx}...`);
    setLoading(true);
    const res = await client.query<LiveShowLogsQuery>({
      query: GET_LIVE_SHOW_LOGS,
      variables: {
        showId,
        pagination: {
          limit: LOGS_PER_PAGE,
          offset: pageIdx * LOGS_PER_PAGE,
        },
      },
    });
    const page = res.data.analytics.liveShowLogs;
    const newPageCount = Math.ceil(page?.totalCount / LOGS_PER_PAGE);
    if (pageCount !== newPageCount) {
      setPageCount(newPageCount);
    }
    setTotalCount(page?.totalCount ?? 0);
    setPages([...pages, page.list]);
    setLoading(false);
    console.log(`Loaded page ${pageIdx}:`, page);
  }, [client, loading, pageCount, pages, showId]);

  const scrollableRef = useRef(null);
  const guardRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    const interObs = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadNextPage();
        }
      },
      {
        root: scrollableRef.current,
      }
    );
    // @ts-ignore
    interObs.observe(guardRef.current);
    return () => interObs.disconnect();
  }, [loadNextPage, scrollableRef, guardRef]);

  const [selectedLog, setSelectedLog] = useState<LiveShowLog | null>(null);
  const [showLogDetails, setShowLogDetails] = useState(false);
  const doShowLogData = (log: LiveShowLog) => {
    setSelectedLog(log);
    setShowLogDetails(true);
  };

  return (
    <div className="mb-4">
      <div className="d-flex mb-2">
        <p className="mb-0">
          Logs ({Math.min(pages.length * LOGS_PER_PAGE, totalCount)} / {totalCount} loaded)
        </p>
        <div style={{ flex: 1 }} />
        <Button onClick={() => setDisplayLogConsole(true)} size="sm" disabled={loading}>
          <i className="fas fa-terminal" style={{ fontSize: '0.95rem', margin: '0px 10px 0 0' }} />
          Open console
        </Button>
      </div>
      <div
        ref={scrollableRef}
        style={{
          borderRadius: '0.35rem',
          fontSize: '0.85rem',
          height: '20em',
          overflowY: 'scroll',
          border: '1px solid rgba(0, 0, 0, 0.125)',
          resize: 'vertical',
        }}
      >
        <ListGroup variant="flush">
          {pages.flatMap((page, pageIdx) =>
            page.map((log: LiveShowLog, logIdx: number) => {
              const variant = { error: 'danger', warn: 'warning' }[log.level];
              const color = { debug: 'text-gray-600', info: 'text-primary' }[log.level];
              const date = new Date(log.timestamp);
              const hasData = Object.entries(log.extraData).length > 0;
              const key = pageIdx * LOGS_PER_PAGE + logIdx;
              return (
                <ListGroup.Item key={key} variant={variant} className={`mb-0 p-1 text-monospace ${color ?? ''}`}>
                  <b title={date.toISOString()}>[{moment(date).format('MMM D HH:mm')}]</b> <b>[{log.source}] </b>
                  {log.message}{' '}
                  {hasData && (
                    // @ts-ignore
                    <span role="button" tabIndex="0" onClick={() => doShowLogData(log)}>
                      <i className="fas fa-info-circle" style={{ fontSize: '0.95rem' }} />
                    </span>
                  )}
                </ListGroup.Item>
              );
            })
          )}
          <ListGroup.Item key="guard" ref={guardRef} variant="dark" className="mb-0 p-2">
            {pages.length < pageCount ? (
              <span>Loading logs... {loading && <Spinner size="sm" animation="border" />}</span>
            ) : (
              'End of logs.'
            )}
          </ListGroup.Item>
        </ListGroup>
      </div>
      <hr />
      <LiveShowLogDetails show={showLogDetails} onHide={() => setShowLogDetails(false)} log={selectedLog} />
      <LiveShowLogsConsolePopup
        showId={showId}
        show={displayLogConsole}
        onHide={() => setDisplayLogConsole(false)}
        onDisplayLogDetails={doShowLogData}
        totalNumberOfLogs={totalCount}
      />
    </div>
  );
};

export default LiveShowLogs;
